import * as React from "react"
import { Check, ChevronsUpDown, Dumbbell, Plus } from "lucide-react"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

const Combobox = React.forwardRef(({
  options = [],
  value,
  onValueChange,
  placeholder = "Выберите или введите упражнение...",
  emptyText = "Упражнение не найдено",
  onCreateNew,
  createNewText = "Добавить в словарь",
  className,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")

  // Синхронизируем inputValue с внешним value
  React.useEffect(() => {
    if (value && typeof value === 'string') {
      setInputValue(value)
    } else if (value && options.length > 0) {
      const selectedOption = options.find(option => option.value === value)
      if (selectedOption) {
        setInputValue(selectedOption.label)
      }
    } else if (!value) {
      setInputValue("")
    }
  }, [value, options])

  const filteredOptions = React.useMemo(() => {
    if (!inputValue) return options
    const q = inputValue.toLowerCase()
    return options.filter(
      (option) =>
        (option.label || '').toLowerCase().includes(q) ||
        (option.value || '').toLowerCase().includes(q)
    )
  }, [options, inputValue])

  const handleSelect = (option) => {
    setInputValue(option.label)
    onValueChange?.(option.value)
    setOpen(false)
  }

  const handleInputChange = (newValue) => {
    setInputValue(newValue)
    // Если пользователь вводит текст, который не соответствует ни одному варианту,
    // передаем этот текст как значение
    if (newValue && !options.find(opt => opt.label === newValue)) {
      onValueChange?.(newValue)
    }
  }

  const handleCreateNew = () => {
    if (onCreateNew && inputValue.trim()) {
      onCreateNew(inputValue.trim())
      setInputValue(inputValue.trim())
      setOpen(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !filteredOptions.length && inputValue.trim()) {
      e.preventDefault()
      handleCreateNew()
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          ref={ref}
          {...props}
        >
          {value ? (
            options.find(option => option.value === value)?.label || value
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput
            placeholder={placeholder}
            value={inputValue}
            onValueChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <CommandList>
            <CommandEmpty>
              <div className="flex flex-col items-center gap-2 py-6">
                <Dumbbell className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{emptyText}</span>
                {inputValue.trim() && onCreateNew && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCreateNew}
                    className="mt-2"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {createNewText} "{inputValue.trim()}"
                  </Button>
                )}
              </div>
            </CommandEmpty>
            {filteredOptions.length > 0 && (
              <>
                {filteredOptions.some(option => option.type === 'standard') && (
                  <CommandGroup heading="Клиенты">
                    {filteredOptions
                      .filter(option => option.type === 'standard')
                      .map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          onSelect={() => handleSelect(option)}
                          className="flex items-center gap-2"
                        >
                          <Dumbbell className="h-3 w-3 text-blue-500" />
                          <span className="flex-1">{option.label}</span>
                          {value === option.value && (
                            <Check className="h-4 w-4 opacity-100" />
                          )}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                )}
                {filteredOptions.some(option => option.type === 'custom') && (
                  <CommandGroup heading="Мой словарь">
                    {filteredOptions
                      .filter(option => option.type === 'custom')
                      .map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          onSelect={() => handleSelect(option)}
                          className="flex items-center gap-2"
                        >
                          <Dumbbell className="h-3 w-3 text-green-500" />
                          <span className="flex-1">{option.label}</span>
                          {value === option.value && (
                            <Check className="h-4 w-4 opacity-100" />
                          )}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
})

Combobox.displayName = "Combobox"

export { Combobox }
